import {
  Behavior,
  CoachingMethod,
  CoachingTactic,
  CoachingTopic,
  DeliveredStatus,
  DurationOptions,
  FollowedSecurityPolicyOptions,
  Metric,
  NoCoachReasons,
  PreparationTimeOptions,
  StatusOptions,
  Tenet,
  Vertical,
} from '@amzn/aet-coachingform-kit/lib/types';
import {
  RecommendedContactStatusOptions,
  ContactReviewItemLabel,
  BarRaiseChoices,
  ContactStatusOptions,
  ResolvedProblemOptions,
  ControllableOptions,
} from '@amzn/aet-contactrecommendation-kit';

import { convertEnumToObject } from '../../Util';
import { getEnvConfigByKey } from '../../config/EnvConfig';
import { Goal } from '@amzn/aet-galactus-kit';

export type AppSyncID = string;
export type AWSDate = string;
export type AWSDateTime = string;

export type BehaviorType = string;
export type CoachingMethodType = string;
export type CoachingTacticType = string;
export type CoachingTopicType = string;
export type DeliveredStatusType = string;
export type DurationOptionsType = string;
export type FollowedSecurityPolicyOptionsType = string;
export type MetricType = string;
export type NoCoachReasonsType = string;
export type PreparationTimeOptionsType = string;
export type StatusOptionsType = string;
export type TenetType = string;
export type VerticalType = string;
export type RecommendedContactStatusOptionsType = string;

export const BehaviorObject = convertEnumToObject(Behavior);
export const CoachingMethodObject = convertEnumToObject(CoachingMethod);
export const CoachingTacticObject = convertEnumToObject(CoachingTactic);
export const CoachingTopicObject = convertEnumToObject(CoachingTopic);
export const DeliveredStatusObject = convertEnumToObject(DeliveredStatus);
export const DurationOptionsObject = convertEnumToObject(DurationOptions);
export const FollowedSecurityPolicyOptionsObject = convertEnumToObject(
  FollowedSecurityPolicyOptions
);
export const ResolvedProblemOptionsObject = convertEnumToObject(
  ResolvedProblemOptions
);

export const ControllableOptionsObject =
  convertEnumToObject(ControllableOptions);

export const RecommendedContactStatusOptionsObject = convertEnumToObject(
  RecommendedContactStatusOptions
);

export const ContactStatusOptionsObject =
  convertEnumToObject(ContactStatusOptions);
export const ContactReviewItemLabelObject = convertEnumToObject(
  ContactReviewItemLabel
);

export const BarRaiseChoicesObject = convertEnumToObject(BarRaiseChoices);

export const MetricObject = convertEnumToObject(Metric);
export const ExtendMetricObject = (): Record<string, string> => {
  if (getEnvConfigByKey('cscEntityPrefix') === 'cscentral_eu_user') {
    const filteredMetric: Record<string, string> = {};
    Object.keys(MetricObject).forEach(key =>
      key === 'ATTENTIVENESS'
        ? (filteredMetric['ATTENTIVENESS'] = 'REASSURING')
        : (filteredMetric[key] = key)
    );
    return filteredMetric;
  }
  return MetricObject;
};

export const NoCoachReasonsObject = convertEnumToObject(NoCoachReasons);
export const PreparationTimeOptionsObject = convertEnumToObject(
  PreparationTimeOptions
);
export const StatusOptionsObject = convertEnumToObject(StatusOptions);
export const TenetObject = convertEnumToObject(Tenet);
export const VerticalObject = convertEnumToObject(Vertical);

export type EnumEmpty = null;
export const enumEmpty: EnumEmpty = null;

export interface CoachingFormUserInput {
  userID: AppSyncID;
  userName: string;
}

export interface CoachingFormUserData {
  userID: AppSyncID;
  userName: string;
  firstName: string;
  fullName: string;
  isManager: boolean;
  employmentStartDate: AWSDateTime;
  lastCoachedDate: AWSDateTime;
}

export interface ContactInput {
  cscContactID: AppSyncID;
  followedSecurityPolicies: FollowedSecurityPolicyOptionsType | EnumEmpty;
  content: string;
  vertical: VerticalType | EnumEmpty;
  contactTime?: {
    calWeek: number;
    calYear: number;
  };
  marketplaceID?: string;
  marketplaceDomainName?: string;
  relatedMetric?: MetricType | EnumEmpty;
}

export const defaultContactReviewValues: ContactInput = {
  cscContactID: '',
  followedSecurityPolicies: enumEmpty,
  content: '',
  vertical: enumEmpty,
  relatedMetric: '',
};

export interface ContactDomainInfo {
  cscContactID: string;
  marketplaceDomainName?: string;
}

export interface CoachingDetailsInput {
  coachingTopic: CoachingTopicType | EnumEmpty;
  relatedContacts: ContactInput['cscContactID'][];
  relatedContactDomains?: ContactDomainInfo[];
  tenets: TenetType[];
  behaviors: BehaviorType[];
  relatedMetric: MetricType | EnumEmpty;
  assignedAction: string;
  status: StatusOptionsType;
}

export const defaultCoachingDetailsValues: CoachingDetailsInput = {
  coachingTopic: enumEmpty,
  relatedContacts: [],
  tenets: [],
  behaviors: [],
  relatedMetric: enumEmpty,
  assignedAction: '',
  status: StatusOptionsObject.PENDING,
};

export interface CoachingSessionInput {
  coachingTactic: CoachingTacticType | EnumEmpty;
  coachingMethod: CoachingMethodType | EnumEmpty;
  duration: DurationOptionsType | EnumEmpty;
  preparationTime: PreparationTimeOptionsType | EnumEmpty;
  coachingSummaryNotes: string;
  managerNotes: string;
}

export const defaultCoachingSessionValues: CoachingSessionInput = {
  managerNotes: '',
  coachingTactic: enumEmpty,
  coachingMethod: enumEmpty,
  duration: enumEmpty,
  preparationTime: enumEmpty,
  coachingSummaryNotes: '',
};

export interface CoachingFormInput {
  coach: CoachingFormUserInput;
  recipient: CoachingFormUserInput;
  deliveredBy?: CoachingFormUserInput;
  reviewedBy?: CoachingFormUserInput;
  closedBy?: CoachingFormUserInput;
  isDelivered: DeliveredStatusType | EnumEmpty;
  deliveredAt?: string;
  reviewedAt?: string;
  closedAt?: string;
  contactReview: ContactInput[];
  coachingDetails: CoachingDetailsInput[];
  coachingSession: CoachingSessionInput;
  reason: NoCoachReasonsType | EnumEmpty;
  otherReason: string;
  coachingId?: string;
  goalIDs?: string[];
}

export interface CoachingFormInputData extends CoachingFormInput {
  id: AppSyncID;
}

export const defaultAWSDateTime = '1970-01-01';
export const defaultAWSYear = 1970;

const defaultCoachingFormUserData: CoachingFormUserData = {
  userID: '',
  userName: '',
  firstName: '',
  fullName: '',
  isManager: false,
  employmentStartDate: defaultAWSDateTime,
  lastCoachedDate: defaultAWSDateTime,
};

export const defaultCoachingFormValues: CoachingFormInputData = {
  id: '',
  coach: { userID: '', userName: '' },
  recipient: { userID: '', userName: '' },
  isDelivered: enumEmpty,
  contactReview: [],
  coachingDetails: [],
  coachingSession: defaultCoachingSessionValues,
  reason: enumEmpty,
  otherReason: '',
  coachingId: '',
};

export const defaultCoachingFormData: CoachingFormData = {
  ...defaultCoachingFormValues,
  coach: defaultCoachingFormUserData,
  recipient: defaultCoachingFormUserData,
  createdAt: defaultAWSDateTime,
  deliveredAt: defaultAWSDateTime,
  updatedAt: defaultAWSDateTime,
};

export interface CoachingFormData extends CoachingFormInputData {
  coach: CoachingFormUserData;
  coachingId?: string;
  recipient: CoachingFormUserData;
  createdAt: AWSDateTime;
  deliveredAt?: AWSDateTime;
  updatedAt: AWSDateTime;
  goals?: Goal[];
}
